import { useEffect, useState } from 'react';

import { cn } from '../../utils';

export interface ToastProps {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  onClose: (id: string) => void;
}

// Icons: *-700 (not *-500) on *-50 bg — *-500 on *-50 gives ~2:1 ❌
// Info title: primary-900 (not 800) — primary-800 on primary-50 = 3.49:1 ❌
const typeStyles = {
  success: {
    container: 'bg-success-50 border-success-200',
    icon: 'text-success-700',
    title: 'text-success-800',
    message: 'text-success-700',
  },
  error: {
    container: 'bg-error-50 border-error-200',
    icon: 'text-error-700',
    title: 'text-error-800',
    message: 'text-error-700',
  },
  warning: {
    container: 'bg-warning-50 border-warning-200',
    icon: 'text-warning-700',
    title: 'text-warning-800',
    message: 'text-warning-700',
  },
  info: {
    container: 'bg-primary-50 border-primary-200',
    icon: 'text-primary-700',
    title: 'text-primary-900',
    message: 'text-primary-800',
  },
};

const icons = {
  success: (
    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  ),
  error: (
    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
        clipRule="evenodd"
      />
    </svg>
  ),
  warning: (
    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
        clipRule="evenodd"
      />
    </svg>
  ),
  info: (
    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
        clipRule="evenodd"
      />
    </svg>
  ),
};

export function Toast({ id, type, title, message, onClose }: ToastProps) {
  const [isVisible, setIsVisible] = useState(false);
  const styles = typeStyles[type];

  useEffect(() => {
    // Trigger animation
    requestAnimationFrame(() => setIsVisible(true));
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(id), 200);
  };

  return (
    <div
      className={cn(
        'pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg border shadow-lg transition-all duration-200',
        styles.container,
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      )}
      role="alert"
    >
      <div className="p-4">
        <div className="flex items-start">
          <div className={cn('flex-shrink-0', styles.icon)}>{icons[type]}</div>
          <div className="ml-3 flex-1">
            <p className={cn('text-sm font-medium', styles.title)}>{title}</p>
            {message && <p className={cn('mt-1 text-sm', styles.message)}>{message}</p>}
          </div>
          <button
            type="button"
            className="ml-4 inline-flex rounded-md p-1.5 text-tkf-fg-subtle hover:bg-tkf-overlay-light focus:outline-none"
            onClick={handleClose}
          >
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

export interface ToastContainerProps {
  toasts: Array<{
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message?: string;
  }>;
  onClose: (id: string) => void;
}

export function ToastContainer({ toasts, onClose }: ToastContainerProps) {
  if (toasts.length === 0) return null;

  return (
    <div className="pointer-events-none fixed bottom-0 right-0 z-tooltip flex flex-col-reverse items-end gap-4 p-4 sm:p-6">
      {toasts.map(toast => (
        <Toast key={toast.id} {...toast} onClose={onClose} />
      ))}
    </div>
  );
}
